export type RuntimeDiagnosticContext = Record<string, unknown>;

export interface RuntimeDiagnosticEvent {
  at: string;
  scope: string;
  kind: "react-boundary" | "window-error" | "unhandled-rejection" | "operation-error";
  name: string | null;
  message: string;
  stack: string | null;
  componentStack: string | null;
  source: string | null;
  line: number | null;
  column: number | null;
}

interface RuntimeDiagnosticStore {
  installedAt: string;
  events: RuntimeDiagnosticEvent[];
  contexts: Record<string, RuntimeDiagnosticContext>;
}

declare global {
  interface Window {
    __FREM_RUNTIME_DIAGNOSTICS__?: RuntimeDiagnosticStore;
  }
}

const MAX_EVENTS = 40;
const MAX_TEXT = 16_000;

function trimText(value: unknown) {
  if (value == null) return null;
  const text = String(value);
  return text.length <= MAX_TEXT ? text : `${text.slice(0, MAX_TEXT)}\n...[truncated]`;
}

function store(): RuntimeDiagnosticStore | null {
  if (typeof window === "undefined") return null;
  if (!window.__FREM_RUNTIME_DIAGNOSTICS__) {
    window.__FREM_RUNTIME_DIAGNOSTICS__ = {
      installedAt: new Date().toISOString(),
      events: [],
      contexts: {}
    };
  }
  return window.__FREM_RUNTIME_DIAGNOSTICS__;
}

function push(event: RuntimeDiagnosticEvent) {
  const target = store();
  if (!target) return;
  target.events.push(event);
  if (target.events.length > MAX_EVENTS) target.events.splice(0, target.events.length - MAX_EVENTS);
}

export function recordRuntimeDiagnosticError(
  scope: string,
  error: unknown,
  options: { kind?: RuntimeDiagnosticEvent["kind"]; componentStack?: string | null; source?: string | null; line?: number | null; column?: number | null } = {}
) {
  const isError = error instanceof Error;
  push({
    at: new Date().toISOString(),
    scope,
    kind: options.kind ?? "react-boundary",
    name: isError ? error.name : null,
    message: trimText(isError ? error.message : error) ?? "Unknown runtime error",
    stack: trimText(isError ? error.stack : null),
    componentStack: trimText(options.componentStack),
    source: trimText(options.source),
    line: options.line ?? null,
    column: options.column ?? null
  });
}

export function setRuntimeDiagnosticContext(scope: string, context: RuntimeDiagnosticContext) {
  const target = store();
  if (!target) return;
  target.contexts[scope] = { ...context, recordedAt: new Date().toISOString() };
}

export function getRuntimeDiagnosticSnapshot() {
  const target = store();
  if (!target) return { installedAt: null, events: [], contexts: {} };
  return {
    installedAt: target.installedAt,
    collectedAt: new Date().toISOString(),
    events: target.events.map((event) => ({ ...event })),
    contexts: Object.fromEntries(Object.entries(target.contexts).map(([key, value]) => [key, { ...value }]))
  };
}

function installGlobalListeners() {
  if (typeof window === "undefined") return;
  const target = store();
  if (!target || (target as RuntimeDiagnosticStore & { globalListenersInstalled?: boolean }).globalListenersInstalled) return;
  (target as RuntimeDiagnosticStore & { globalListenersInstalled?: boolean }).globalListenersInstalled = true;

  window.addEventListener("error", (event) => {
    recordRuntimeDiagnosticError("window", event.error ?? event.message, {
      kind: "window-error",
      source: event.filename || null,
      line: event.lineno || null,
      column: event.colno || null
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordRuntimeDiagnosticError("window", event.reason, { kind: "unhandled-rejection" });
  });
}

installGlobalListeners();
